import { axiosInstance } from '../../../api/axiosConfig';
import { downloadBlobResponse } from '@/utils/fileDownload';
import { savApi as sharedSavApi } from '@/features/savs/api/savApi';
import type { VirtualStatusInfo } from '../types/sav.types';
import { ISavV2 } from '@/models/savV2';
import type { Message } from '@/Api/sav-v2/savV2MessagesApi';
import type { IListResult } from '@/models/IListResult';

export const savApi = {
  ...sharedSavApi,

  getById: async (id: number): Promise<ISavV2> => {
    const { data } = await axiosInstance.get<ISavV2>(`/sav-v2/${id}`);
    return data;
  },

  getVirtualStatuses: async (id: number): Promise<VirtualStatusInfo[]> => {
    const { data } = await axiosInstance.get<{ virtualStatuses: VirtualStatusInfo[] }>(`/sav-v2/${id}/virtual-statuses`);
    return data.virtualStatuses || [];
  },

  getBeneficiaryMessages: async (savId: number, page = 0, total = 40): Promise<IListResult<Message>> => {
    const { data } = await axiosInstance.get<IListResult<Message>>(`/sav-v2/${savId}/messages/beneficiary?page=${page}&total=${total}`);
    return data;
  },

  getInternalMessages: async (savId: number, page = 0, total = 40): Promise<IListResult<Message>> => {
    const { data } = await axiosInstance.get<IListResult<Message>>(`/sav-v2/${savId}/messages/internal?page=${page}&total=${total}`);
    return data;
  },

  getGarantMessages: async (savId: number, page = 0, total = 40): Promise<IListResult<Message>> => {
    const { data } = await axiosInstance.get<IListResult<Message>>(`/sav-v2/${savId}/messages/garant?page=${page}&total=${total}`);
    return data;
  },

  sendGarantMessage: async (savId: number, content: { text: string; html?: string; subject?: string; attachmentsIds?: number[] }): Promise<void> => {
    await axiosInstance.post(`/sav-v2/${savId}/messages/garant`, { content });
  },

  uploadDocument: async (
    savId: number,
    file: File,
    label: string,
    isInternal = true,
  ): Promise<{ success: boolean; document: { id: number } }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('label', label);
    formData.append('isInternal', isInternal ? 'true' : 'false');
    const { data } = await axiosInstance.post(`/sav-v2/${savId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return data;
  },

  /**
   * Prise en charge directe par le GARANT (workflow direct-coverage : garant =
   * réparateur, awaitingGarant sans décision). Le garant saisit lui-même les
   * montants (publics + internes) et clôture le SAV. Endpoint garant-scoped
   * (@OwnedResource sav, policy guarantor-strict) côté api, distinct du endpoint
   * gestionnaire `/direct-coverage`. Montants en centimes. La facture éventuelle
   * est attachée séparément par l'endpoint d'upload (pas via ce payload). Réponse : SAV clôturé.
   */
  submitGarantDirectCoverage: async (
    savId: number,
    interventionId: string,
    payload: {
      beneficiaryAmount: number; // centimes
      coveredAmount: number; // centimes
      commercialAmount?: number; // centimes
      internalCoveredAmount?: number; // centimes
      internalCommercialAmount?: number; // centimes
      internalComment?: string;
    },
  ): Promise<{ success: boolean }> => {
    const { data } = await axiosInstance.post(
      `/sav-v2/${savId}/interventions/${interventionId}/garant-direct-coverage`,
      payload,
    );
    return data;
  },

  markGarantMessagesProcessed: async (savId: number): Promise<void> => {
    await axiosInstance.post(`/sav-v2/${savId}/messages/garant/mark-processed`);
  },

  /** Compteur de messages garant non lus (badge « Mes SAV » de la sidebar). */
  countUnreadGarantMessages: async (): Promise<number> => {
    const { data } = await axiosInstance.get<{ count: number }>(
      '/pro-v2/sav/garant-messages/unread-count',
    );
    return data.count;
  },

  /** Le garant a lu le fil (ouverture du dossier) → remet son compteur à 0. */
  markGarantMessagesRead: async (savId: number): Promise<void> => {
    await axiosInstance.post(`/sav-v2/${savId}/messages/garant/mark-read`);
  },

  getDocumentDownloadToken: async (savId: number, documentId: number): Promise<string> => {
    const { data } = await axiosInstance.get<{ documentToken: string }>(`/sav-v2/${savId}/documents/${documentId}/get-download-token`);
    return data.documentToken;
  },

  /**
   * Génère à la volée un lien de validation garant (token JWT 7j) et le renvoie.
   * Le garant (permission `sav.sign`) ouvre ce lien pour consulter et signer la
   * décision sur la page de validation dédiée — au lieu de valider en place.
   * Endpoint authentifié + cloisonné (@OwnedResource sav).
   */
  getGarantValidationLink: async (
    savId: number,
  ): Promise<{ validationUrl: string; validationToken: string }> => {
    const { data } = await axiosInstance.get<{
      success: boolean;
      validationUrl: string;
      validationToken: string;
    }>(`/sav-v2/${savId}/garant-validation-link`);
    return data;
  },

  /**
   * Télécharge un document SAV. `fileName` est le nom lisible souhaité (avec
   * ou sans extension) : l'extension est recalée sur le type réel du fichier.
   */
  downloadDocument: async (savId: number, documentId: number, fileName?: string): Promise<void> => {
    const token = await savApi.getDocumentDownloadToken(savId, documentId);
    const response = await axiosInstance.get<Blob>(
      `/documents/${documentId}/download?downloadType=download&documentDownloadToken=${token}`,
      { responseType: 'blob' },
    );
    downloadBlobResponse(response, fileName);
  },

  getActionRequiredCount: async (action: string): Promise<number> => {
    const { data } = await axiosInstance.get<{ count: number }>(`/pro-v2/sav/count-action-required?action=${action}`);
    return data.count;
  },
};
